import { memo, useState } from "react";
import { useContent } from "../../../../../../../contexts/contentContext/contentContext";
import { useParams } from "react-router-dom";
import {
  CurrencySymbolE,
  PlanPackagesE,
  SelectedOptionE,
} from "../../../../models/enums";
import usePurchases from "../../../../hooks/usePurchases";
import useCurrency from "../../../../hooks/useCurrency";
import getPrices from "../../../../helpers/getPrices";
import getPricePerCode from "../../helpers/getPricePerCode";
import LoadingImageElement from "../../../../../../Elements/LoadingContainerElement/LoadingImageElement";
import type { PurchaseParamsT } from "../../../../models/models";

import styles from "./styles.module.scss";

interface OptionI {
  type: SelectedOptionE;
}

const FULL_YEAR_MONTHS_AMOUNT = 12;

const Option = memo(({ type }: OptionI) => {
  const { packageType } = useParams<PurchaseParamsT>();
  const {
    selectOption,
    isOptionSelected,
    getPlanByName,
    isFetchingActiveJMPlans,
    completedStep,
  } = usePurchases();
  const { getTextByKey, getDirection } = useContent();
  const { currency } = useCurrency();
  const plan = getPlanByName(packageType as unknown as PlanPackagesE);
  const [isFocused, setIsFocused] = useState(false);

  // Get prices for selected plan or amount of codes in different currency
  const prices = getPrices(plan?.Prices!, currency);

  const handleKeyDown = (e: KeyboardEvent) => {
    if (e.key === "Enter" && isFocused) {
      selectOption(type);
    }
  };

  return (
    <div
      className={`${styles.wrapper} ${
        // Here I'm intentionally using not strict equality
        isOptionSelected(type) ? styles.selected : ""
      }`}
      // Don't allow to select option with the tab key
      // if user clicked "Next" button
      tabIndex={completedStep === 0 ? 0 : -1}
      onClick={() => selectOption(type)}
      onFocus={() => setIsFocused(true)}
      onBlur={() => setIsFocused(false)}
      onKeyDown={(e) => handleKeyDown(e as unknown as KeyboardEvent)}
    >
      {(type === SelectedOptionE.YEARLY ||
        type === SelectedOptionE.TEN_CODES) && (
        <>
          <div className={styles.stripe} dir={getDirection()} />
          <span className={styles.saveText} dir={getDirection()}>
            {getTextByKey(
              packageType !== "pay-as-you-go"
                ? "save-17-percents"
                : "most-popular"
            )}
          </span>
        </>
      )}
      <div className={styles.circleWrapper}>
        {!isFetchingActiveJMPlans ? (
          <div className={styles.circle}>
            <span
              className={
                getDirection() === "rtl" ? styles.priceRtl : styles.price
              }
            >
              {packageType === "pay-as-you-go"
                ? // Show amount of codes
                  type
                : type === "yearly"
                ? // Show price per month calculated from yearly price
                  Math.floor(prices.yearly / FULL_YEAR_MONTHS_AMOUNT).toFixed(0)
                : // Show monthly price
                  prices.monthly}{" "}
              {packageType !== "pay-as-you-go" ? (
                <span className={styles.currency}>
                  {CurrencySymbolE[currency]}
                </span>
              ) : null}
            </span>
            <span className={styles.description}>
              {getTextByKey(
                !(packageType === "pay-as-you-go")
                  ? "payment-method-per-month"
                  : "purchase-codes-text"
              )}
            </span>
          </div>
        ) : (
          <div className={styles.circle}>
            <LoadingImageElement />
          </div>
        )}
      </div>
      {!isFetchingActiveJMPlans ? (
        <div className={styles.bottomText}>
          {packageType !== "pay-as-you-go" ? (
            type === "yearly" ? (
              <>
                <strong>
                  {getDirection() === "rtl" && CurrencySymbolE[currency]}
                  {prices.yearly}
                  {getDirection() === "ltr" && CurrencySymbolE[currency]}
                </strong>{" "}
                {getTextByKey("billed-yearly-purchase")}
              </>
            ) : (
              getTextByKey("billed-monthly")
            )
          ) : (
            <>
              <div className={styles.codesTotalPrice}>
                <span className="">
                  {getDirection() === "rtl" && CurrencySymbolE[currency]}
                  {prices[type]}
                  {getDirection() === "ltr" && CurrencySymbolE[currency]}
                </span>
              </div>
              <span>
                {getDirection() === "rtl" && CurrencySymbolE[currency]}
                {getPricePerCode(prices[type]!, type as number)}
                {getDirection() === "ltr" && CurrencySymbolE[currency]}{" "}
                {getTextByKey("per-code")}
              </span>
            </>
          )}
        </div>
      ) : null}
    </div>
  );
});

export default Option;
